import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes } from "react-router-dom";
import { api, type Bot } from "@/lib/api";
import { Shell } from "@/components/shell";
import { ErrorNote, Loading } from "@/components/primitives";
import { OverviewPage } from "@/pages/overview";
import { PlayerDetailPage, PlayersPage } from "@/pages/players";
import { SourcesPage } from "@/pages/sources";
import { GroupsPage } from "@/pages/groups";
import { AuditLogPage, SystemLogPage } from "@/pages/logs";
import { AdminsPage, BotsPage } from "@/pages/account";
import { BroadcastsPage } from "@/pages/broadcasts";
import { AutoRepliesPage } from "@/pages/auto-replies";
import { CommandsPage } from "@/pages/commands";
import { ImportPage } from "@/pages/import";
import { FooterPage } from "@/pages/footer";
import { LanguagesPage } from "@/pages/languages";
import { HelpPage } from "@/pages/help";
import { RolesPage } from "@/pages/roles";
import { LoginPage } from "@/pages/login";
import { SetupPage } from "@/pages/setup";
import { useMe } from "@/lib/shell-context";
import { allows, type Module, type ModuleLevel } from "@kickwin/shared";

/** Bot pages in nav order, for landing on the first one a role may open. */
const LANDING: [string, Module, ModuleLevel][] = [
  ["", "overview", "view"],
  ["players", "players", "view"],
  ["broadcasts", "broadcasts", "view"],
  ["sources", "sources", "view"],
  ["auto-replies", "autoReplies", "view"],
  ["commands", "commands", "view"],
  ["groups", "groups", "view"],
  ["events", "systemLog", "view"],
  ["audit", "auditLog", "view"],
];

/** Sends the operator to their first bot, on the first page their role opens. */
function Landing() {
  const me = useMe();
  const bots = useQuery({
    queryKey: ["bots"],
    queryFn: () => api.get<Bot[]>("/bots"),
    retry: false,
  });

  if (bots.isLoading) return <Loading />;
  if (bots.error) return <ErrorNote error={bots.error} />;

  const first = bots.data?.[0];
  const page = LANDING.find(([, module, level]) => allows(me.permissions, module, level as never));
  if (first && page)
    return <Navigate to={`/bots/${first.id}/${page[0]}`.replace(/\/$/, "")} replace />;
  if (allows(me.permissions, "bots", "view")) return <Navigate to="/bots" replace />;
  if (allows(me.permissions, "admins", "view")) return <Navigate to="/admins" replace />;
  return (
    <div className="mx-auto max-w-md py-16 text-center text-muted-foreground">
      Your role does not open any page yet. Ask the owner to give you access to a bot.
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/*
        Outside the Shell on purpose: the Shell loads /api/me, which is
        exactly what these two pages exist to make possible. Inside it they
        would bounce off the refusal that sent the person here.
      */}
      <Route path="login" element={<LoginPage />} />
      <Route path="setup" element={<SetupPage />} />

      <Route element={<Shell />}>
        <Route index element={<Landing />} />
        <Route path="bots" element={<BotsPage />} />
        <Route path="admins" element={<AdminsPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="bots/:botId">
          <Route index element={<OverviewPage />} />
          <Route path="players" element={<PlayersPage />} />
          <Route path="players/:playerId" element={<PlayerDetailPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="broadcasts" element={<BroadcastsPage />} />
          <Route path="auto-replies" element={<AutoRepliesPage />} />
          <Route path="commands" element={<CommandsPage />} />
          <Route path="import" element={<ImportPage />} />
          <Route path="footer" element={<FooterPage />} />
          <Route path="languages" element={<LanguagesPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="events" element={<SystemLogPage />} />
          <Route path="audit" element={<AuditLogPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
